import { aiChatApi, aiProcessStepBlobsApi, aiStatsApi, aiUsageMetricsApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { GROUP_KIND } from './aiUsage.constants';

export const AI_USAGE_ACTION_TYPE = createNamedActions('aiUsage', {
  GET_AI_USAGE_GROUPS: 'GET_AI_USAGE_GROUPS',
  GET_AI_USAGE_SUMMARY: 'GET_AI_USAGE_SUMMARY',
  GET_AI_USAGE_GROUP_ITEMS: 'GET_AI_USAGE_GROUP_ITEMS',
  GET_AI_USAGE_CALLS: 'GET_AI_USAGE_CALLS',
  GET_CHAT_SESSION_MESSAGES: 'GET_CHAT_SESSION_MESSAGES',
  GET_PROCESS_STEP_INPUT: 'GET_PROCESS_STEP_INPUT',
  GET_PROCESS_STEP_OUTPUT: 'GET_PROCESS_STEP_OUTPUT',
  GET_PROCESS_STEP_ERROR: 'GET_PROCESS_STEP_ERROR',
  GET_PROCESS_STEP_SYSTEM_PROMPT: 'GET_PROCESS_STEP_SYSTEM_PROMPT',
  GET_AI_STATS_COST_SERIES: 'GET_AI_STATS_COST_SERIES',
  GET_AI_STATS_COST_LEADERBOARD: 'GET_AI_STATS_COST_LEADERBOARD',
  GET_AI_STATS_FEEDBACK: 'GET_AI_STATS_FEEDBACK',
  RESET_AI_USAGE_STATE: 'RESET_AI_USAGE_STATE',
});

export const getAiUsageGroupsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiUsageMetricsApi.getAiUsageByCompanyGrouped(params);

  return dispatch({
    type: AI_USAGE_ACTION_TYPE.GET_AI_USAGE_GROUPS,
    payload: data,
  });
});

export const getAiUsageSummaryAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiUsageMetricsApi.getAiUsageSummary(params);

  return dispatch({
    type: AI_USAGE_ACTION_TYPE.GET_AI_USAGE_SUMMARY,
    payload: data,
  });
});

const fetchGroupItems = async ({ kind, groupId }) => {
  if (kind === GROUP_KIND.EXTRACT) {
    const { data } = await aiUsageMetricsApi.getAiUsageByExtractionSession(groupId);

    return data;
  }

  if (kind === GROUP_KIND.CHAT) {
    const { data } = await aiUsageMetricsApi.getAiUsageByChatSession(groupId);

    return data;
  }

  if (kind === GROUP_KIND.EXEC) {
    const { data } = await aiUsageMetricsApi.getAiUsageByExecution(groupId);

    return data;
  }

  return [];
};

export const getAiUsageGroupItemsAction = catchAsyncActionError(
  ({ groupKey, kind, groupId }) =>
    async dispatch => {
      const items = await fetchGroupItems({ groupKey, kind, groupId });

      return dispatch({
        type: AI_USAGE_ACTION_TYPE.GET_AI_USAGE_GROUP_ITEMS,
        payload: { groupKey, items },
      });
    },
);

export const getAiUsageCallsAction = catchAsyncActionError(metricsId => async dispatch => {
  const { data } = await aiUsageMetricsApi.getAiUsageMetricsCalls(metricsId);

  return dispatch({
    type: AI_USAGE_ACTION_TYPE.GET_AI_USAGE_CALLS,
    payload: { metricsId, items: data },
  });
});

export const getChatSessionMessagesAction = catchAsyncActionError(
  ({ loanId, sessionId }) =>
    async dispatch => {
      const { data } = await aiChatApi.getMessages({ loanId });

      return dispatch({
        type: AI_USAGE_ACTION_TYPE.GET_CHAT_SESSION_MESSAGES,
        payload: { sessionId, messages: data },
      });
    },
);

export const getProcessStepInputAction = catchAsyncActionError(
  stepExecutionId => async dispatch => {
    const { data } = await aiProcessStepBlobsApi.getInput(stepExecutionId);

    return dispatch({
      type: AI_USAGE_ACTION_TYPE.GET_PROCESS_STEP_INPUT,
      payload: { stepExecutionId, data },
    });
  },
);

export const getProcessStepOutputAction = catchAsyncActionError(
  stepExecutionId => async dispatch => {
    const { data } = await aiProcessStepBlobsApi.getOutput(stepExecutionId);

    return dispatch({
      type: AI_USAGE_ACTION_TYPE.GET_PROCESS_STEP_OUTPUT,
      payload: { stepExecutionId, data },
    });
  },
);

export const getProcessStepErrorAction = catchAsyncActionError(
  stepExecutionId => async dispatch => {
    const { data } = await aiProcessStepBlobsApi.getError(stepExecutionId);

    return dispatch({
      type: AI_USAGE_ACTION_TYPE.GET_PROCESS_STEP_ERROR,
      payload: { stepExecutionId, data },
    });
  },
);

export const getProcessStepSystemPromptAction = catchAsyncActionError(
  stepExecutionId => async dispatch => {
    const { data } = await aiProcessStepBlobsApi.getSystemPrompt(stepExecutionId);

    return dispatch({
      type: AI_USAGE_ACTION_TYPE.GET_PROCESS_STEP_SYSTEM_PROMPT,
      payload: { stepExecutionId, data },
    });
  },
);

export const getAiStatsCostSeriesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiStatsApi.getCost(params);

  return dispatch({
    type: AI_USAGE_ACTION_TYPE.GET_AI_STATS_COST_SERIES,
    payload: data,
  });
});

export const getAiStatsCostLeaderboardAction = catchAsyncActionError(
  ({ dimension, params }) =>
    async dispatch => {
      const { data } = await aiStatsApi.getCost(params);

      return dispatch({
        type: AI_USAGE_ACTION_TYPE.GET_AI_STATS_COST_LEADERBOARD,
        payload: { dimension, items: data },
      });
    },
);

export const getAiStatsFeedbackAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await aiStatsApi.getFeedback(params);

  return dispatch({
    type: AI_USAGE_ACTION_TYPE.GET_AI_STATS_FEEDBACK,
    payload: data,
  });
});

export const resetAiUsageStateAction = () => ({
  type: AI_USAGE_ACTION_TYPE.RESET_AI_USAGE_STATE,
});
